import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TbDeviceFloppy, TbPlugConnected, TbPlugConnectedX, TbTrash, TbCircleCheck,
  TbAlertTriangle, TbSettings, TbPlus, TbRefresh
} from 'react-icons/tb';
import api from '../../services/server-api/api';
import '../../../assets/css/integrations.css';

const getError = (error, fallback) => error.response?.data?.error || fallback;

const buildForm = (provider) => {
  const settings = provider.config?.settings || {};
  const values = {};
  provider.settingsFields.forEach(field => {
    values[field.key] = settings[field.key] || field.default || '';
  });
  return { apiKey: '', settings: values };
};

// Modal with the account-level settings of a provider (API key + provider settings)
const ConfigureModal = ({ provider, onClose, onSaved, notify }) => {
  const config = provider.config;
  const [form, setForm] = useState(() => buildForm(provider));
  const [busy, setBusy] = useState('');

  const setSetting = (key, value) => {
    setForm(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
  };

  const renderSettingInput = (field) => {
    if (field.type === 'select') {
      return (
        <select
          className="integration-input"
          value={form.settings[field.key]}
          onChange={(e) => setSetting(field.key, e.target.value)}
        >
          {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }

    return (
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        className="integration-input"
        value={form.settings[field.key]}
        onChange={(e) => setSetting(field.key, e.target.value)}
        placeholder={field.default || ''}
      />
    );
  };

  const save = async () => {
    const response = await api.put(`/api/integrations/${provider.id}`, form);
    setForm(prev => ({ ...prev, apiKey: '' }));
    await onSaved();
    return response;
  };

  const handleSave = async () => {
    setBusy('save');
    try {
      const response = await save();
      notify(response.data.message, response.data.warning ? 'error' : 'success');
      if (!response.data.warning) onClose();
    } catch (error) {
      notify(getError(error, 'Failed to save'), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleRefresh = async () => {
    setBusy('sync');
    try {
      const response = await api.post(`/api/integrations/${provider.id}/sync`);
      notify(response.data.message, 'success');
      await onSaved();
    } catch (error) {
      notify(getError(error, 'Failed to reload'), 'error');
    } finally {
      setBusy('');
    }
  };

  const brands = config?.syncedData?.brands || [];

  // Saves first so the connection is tested with what is on screen
  const handleSaveAndTest = async () => {
    setBusy('test');
    try {
      await save();
      const response = await api.post(`/api/integrations/${provider.id}/test`);
      notify(response.data.message, response.data.warning ? 'error' : 'success');
    } catch (error) {
      notify(getError(error, 'Connection failed'), 'error');
    } finally {
      await onSaved();
      setBusy('');
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="modal-content integration-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
      >
        <button className="modal-close-button" onClick={onClose}>✕</button>
        <div className="integration-modal-title">
          <span className="integration-logo">{provider.name.charAt(0)}</span>
          <h3>{provider.name}</h3>
        </div>

        <div className="integration-field">
          <label>API Key <span className="required">*</span></label>
          <input
            type="password"
            autoComplete="new-password"
            className="integration-input"
            value={form.apiKey}
            onChange={(e) => setForm(prev => ({ ...prev, apiKey: e.target.value }))}
            placeholder={config?.hasApiKey ? `Saved: ${config.maskedApiKey} — leave empty to keep` : 'Paste your API key'}
          />
        </div>

        {provider.settingsFields.map(field => (
          <div className="integration-field" key={field.key}>
            <label>
              {field.label} {field.required && <span className="required">*</span>}
            </label>
            {renderSettingInput(field)}
            {field.help && <small className="integration-help">{field.help}</small>}
          </div>
        ))}

        {config?.hasApiKey && provider.id === 'optimove' && (
          <div className="integration-field">
            <label className="integration-synced-label">
              Brands from {provider.name}
              <button type="button" className="integration-btn" onClick={handleRefresh} disabled={!!busy}>
                <TbRefresh /> {busy === 'sync' ? 'Loading...' : 'Refresh'}
              </button>
            </label>
            {brands.length > 0 ? (
              <div className="integration-synced-list">
                {brands.map(brand => <span key={brand.id}>{brand.name} <small>ID {brand.id}</small></span>)}
              </div>
            ) : (
              <small className="integration-help">
                {config.lastSyncedAt ? `No email brands found in ${provider.name}.` : 'Not loaded yet — press Refresh.'}
              </small>
            )}
            {config.lastSyncError && <small className="integration-error">{config.lastSyncError}</small>}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-cancel" onClick={onClose} disabled={!!busy}>Cancel</button>
          <button className="btn-cancel" onClick={handleSaveAndTest} disabled={!!busy}>
            <TbPlugConnected />&nbsp;{busy === 'test' ? 'Testing...' : 'Save & Test'}
          </button>
          <button className="btn-save" onClick={handleSave} disabled={!!busy}>
            <TbDeviceFloppy />&nbsp;{busy === 'save' ? 'Saving...' : 'Save'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const IntegrationCard = ({ provider, onConfigure, onChange, notify }) => {
  const config = provider.config;
  const [busy, setBusy] = useState(false);

  const run = async (request) => {
    setBusy(true);
    try {
      const response = await request();
      notify(response.data?.message, 'success');
      await onChange();
    } catch (error) {
      notify(getError(error, 'Request failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = () => run(
    () => api.post(`/api/integrations/${provider.id}/${config?.isActive ? 'deactivate' : 'activate'}`)
  );

  const handleRemove = () => {
    if (!window.confirm(`Disconnect ${provider.name}? The saved API key will be deleted.`)) return;
    run(() => api.delete(`/api/integrations/${provider.id}`));
  };

  return (
    <motion.div
      className={`integration-card ${config?.isActive ? 'active' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -3 }}
    >
      <div className="integration-card-top">
        <span className="integration-logo">{provider.name.charAt(0)}</span>
        <div className="integration-badges">
          {config?.isActive && <span className="integration-badge active">Active</span>}
          {config && !config.isActive && <span className="integration-badge muted">Connected</span>}
          {config?.lastTestedAt && (
            <span
              className={`integration-badge ${config.lastTestOk ? 'ok' : 'failed'}`}
              title={`Tested ${new Date(config.lastTestedAt).toLocaleString()}`}
            >
              {config.lastTestOk ? <TbCircleCheck /> : <TbAlertTriangle />}
            </span>
          )}
        </div>
      </div>

      <h3>{provider.name}</h3>
      <p className="integration-description">{provider.description}</p>
      <div className="integration-channels">
        {provider.supportedChannels.map(channel => <span key={channel}>{channel}</span>)}
        {Array.isArray(config?.syncedData?.brands) && <span>{config.syncedData.brands.length} brand(s)</span>}
      </div>

      <div className="integration-card-actions">
        {config ? (
          <>
            <button className="integration-btn" onClick={onConfigure} disabled={busy}>
              <TbSettings /> Configure
            </button>
            <button
              className={`integration-btn ${config.isActive ? '' : 'success'}`}
              onClick={handleToggleActive}
              disabled={busy}
            >
              {config.isActive ? <><TbPlugConnectedX /> Deactivate</> : <><TbPlugConnected /> Activate</>}
            </button>
            <button className="integration-btn danger icon-only" onClick={handleRemove} disabled={busy} title="Disconnect">
              <TbTrash />
            </button>
          </>
        ) : (
          <button className="integration-btn primary" onClick={onConfigure}>
            <TbPlus /> Connect
          </button>
        )}
      </div>
    </motion.div>
  );
};

const Integrations = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const notify = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
  };

  const fetchProviders = async () => {
    try {
      const response = await api.get('/api/integrations/providers');
      setProviders(response.data);
    } catch (error) {
      notify(getError(error, 'Failed to load integrations'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const activeProvider = providers.find(p => p.config?.isActive);
  const configuringProvider = providers.find(p => p.id === configuring);

  return (
    <div className="integrations-container">
      <div className="integrations-header">
        <div>
          <h2>Integrations</h2>
          <p className="integrations-subtitle">
            Connect a CRM to push creatives as templates. Only one CRM can be active at a time.
          </p>
        </div>
        <span className={`integration-badge ${activeProvider ? 'active' : 'muted'}`}>
          Active CRM: {activeProvider ? activeProvider.name : 'None'}
        </span>
      </div>

      {loading ? (
        <div className="integrations-loading">Loading integrations...</div>
      ) : (
        <div className="integrations-grid">
          {providers.map(provider => (
            <IntegrationCard
              key={provider.id}
              provider={provider}
              onConfigure={() => setConfiguring(provider.id)}
              onChange={fetchProviders}
              notify={notify}
            />
          ))}
          <div className="integration-card placeholder">
            <TbPlus className="integration-placeholder-icon" />
            <h3>More integrations</h3>
            <p className="integration-description">Additional CRM platforms will appear here.</p>
          </div>
        </div>
      )}

      <AnimatePresence>
        {configuringProvider && (
          <ConfigureModal
            provider={configuringProvider}
            onClose={() => setConfiguring(null)}
            onSaved={fetchProviders}
            notify={notify}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {notification.show && (
          <motion.div
            className={`integration-notification ${notification.type}`}
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
          >
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Integrations;
